import {connect} from "react-redux";
import {Box, Button, FormControl, InputLabel, List, ListItem, ListItemText, MenuItem, Select, SelectChangeEvent, Switch,
    TextField} from "@mui/material";
import {selectConstants, selectTags} from "../../redux/selectors/firebase";
import {IConstant} from "../dialogs/project/Constant";
import React, {ChangeEventHandler, FocusEventHandler, FunctionComponent, useContext, useState} from "react";
import {FirebaseContext} from "../firebase/FirebaseProvider";
import { updateConstraintData } from "../../utility/constantsTagsUtils";

interface ITagValueProps {
    set: (value: any) => void
}
interface IStringTagValueProps extends ITagValueProps {
    value: string
}
function TextTagValue({set, value}: IStringTagValueProps) {
    const handleValueBlur: FocusEventHandler<HTMLInputElement> = ({target}) =>
        set(target.value);

    return (
        <TextField label="Value" defaultValue={value} size="small" onBlur={handleValueBlur} sx={{width: "50%"}}/>
    );
}
interface INumberTagValueProps extends ITagValueProps {
    value: number
}
function NumberTagValue({set, value}: INumberTagValueProps) {
    const handleValueBlur: FocusEventHandler<HTMLInputElement> = ({target}) =>
        set(target.value);

    return (
        <TextField label="Value" defaultValue={value} size="small" onBlur={handleValueBlur} sx={{width: "50%"}} type="number"/>
    );
}
interface IBooleanTagValueProps extends ITagValueProps {
    value: boolean
}
function BooleanTagValue({set, value}: IBooleanTagValueProps) {
    const handleValueChange: ChangeEventHandler<HTMLInputElement> = ({target}) =>
        set(target.checked);

    return (
        <Switch checked={value} onChange={handleValueChange}/>
    );
}
interface ISelectTagValueProps extends IStringTagValueProps {
    values: {[key: string]: string}
}
function SelectTagValue({set, value, values}: ISelectTagValueProps) {
    const handleValueChange = ({target}: SelectChangeEvent) =>
        set(target.value);

    return (
        <FormControl sx={{width: "50%"}}>
            <InputLabel>Tag Type</InputLabel>
            <Select label="Tag Type" value={value} size="small" onChange={handleValueChange}>
                {Object.keys(values).map(value =>
                    <MenuItem key={value} value={value}>{values[value]}</MenuItem>
                )}
            </Select>
        </FormControl>
    );
}

export interface ITag {
    id?: string
    type: string
    value: string | number | boolean
}
export type Tags = {[state: string]: {[key: string]: ITag }}
export type Constants = {[key: string]: IConstant}

interface IProps {
    constants: Constants
    project: string
    state: string
    tags: Tags
}
function StateTags({constants, project, state, tags}: IProps) {
    const filterUniqueConstants = (constantKey: string) => {
        const constant = constants[constantKey];

        if (constant.constraints) {
            const unique = Object.keys(constant.constraints).find(constraintKey =>
                constant.constraints![constraintKey].type === "unique");

            if (unique) {
                const stateTags = tags[state] || {};

                return Object.keys(stateTags).find(tagKey => stateTags[tagKey].type === constantKey) === undefined;
            }
        }
        return true;
    };
    const constantTypes = Object.keys(constants || {}).filter(filterUniqueConstants);
    const defaultConstantType = constantTypes.length ? constantTypes[0] : "";
    const [addTag, setAddTag] = useState(defaultConstantType);
    const {push, remove, set} = useContext(FirebaseContext);
    const handleAddTag = () =>{
        const type = addTag;
        const value = constants[addTag]!.type;
        push(`tags/${project}/${state}`, {type, value})
            .then(() => setAddTag(defaultConstantType))
            .then(()=> updateConstraintData(type, value, constants, project, set));
    }

    const handleAddTagChange = ({target}: SelectChangeEvent) =>
        setAddTag(target.value);
    const deleteTag = (key: string) =>
        remove(`tags/${project}/${state}/${key}`);
    const stateTags = (tags && tags[state]) || {};
    const tagValueComponents: {[type: string]: FunctionComponent<any>} = {
        boolean: BooleanTagValue,
        default: TextTagValue,
        number: NumberTagValue,
        select: SelectTagValue
    }

    return (
        <Box m={1}>
            <Box display="flex">
                <FormControl fullWidth>
                    <InputLabel>Tag Type</InputLabel>
                    <Select label="Tag Type" value={addTag} size="small" onChange={handleAddTagChange}>
                        {constantTypes.map(constant =>
                            <MenuItem key={constant} value={constant}>{constant}</MenuItem>
                        )}
                    </Select>
                </FormControl>
                <Button onClick={handleAddTag} variant="contained" sx={{marginLeft: 1}}>Add</Button>
            </Box>
            <List>
                {Object.keys(stateTags).map(key => {
                    const tag = stateTags[key];
                    const constant = constants[tag.type];

                    const handleSetTag = (value: unknown) =>{
                        set(`tags/${project}/${state}/${key}/value`, tag.value)
                            .then(()=>updateConstraintData(tag.type, tag.value, constants, project, set))
                    }
                    
                    if (constant) {
                        const constraintKeys = Object.keys(constant.constraints || {});
                        const selectIndex = constraintKeys.find(constraint =>
                            constant.constraints![constraint].type === "select");
                        const values = selectIndex && constant.constraints![selectIndex].data.values;
                        const valueType = selectIndex ? "select" : constant.type;

                        return (
                            <ListItem key={key}>
                                <ListItemText title={constant.description} primary={constant.name || tag.type}/>
                                {React.createElement(tagValueComponents[valueType] || tagValueComponents.default,
                                    {project, state, id: key, set: handleSetTag, value: tag.value, values})}
                                <Button onClick={() => deleteTag(key)} variant="contained" sx={{marginLeft: 1}}>
                                    Delete
                                </Button>
                            </ListItem>
                        );
                    } else {
                        return (
                            <ListItem key={key}>
                                <ListItemText primary={`The type "${stateTags[key].type}" for this tag has been deleted`}/>
                                <Button onClick={() => deleteTag(key)} variant="contained" sx={{marginLeft: 1}}>
                                    Delete
                                </Button>
                            </ListItem>
                        );
                    }
                })}
            </List>
        </Box>
    );
}
const mapStateToProps = (store: never) => ({
    constants: selectConstants(store) || {},
    tags: selectTags(store) || {},
});

export default connect(mapStateToProps, {
})(StateTags);
